<script setup lang="ts">
  import { message } from "ant-design-vue";
  import ViewerJS from "viewerjs";
  import "viewerjs/dist/viewer.css";
  import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
  import Markdown from "~/components/ai/main/model/Markdown.vue";
  import Common from "~/utils/common";
  import type { EventSourceMessage } from "@microsoft/fetch-event-source";

  // 定义组件属性
  interface Props {
    visible?: boolean;
  }

  // 定义消息类型
  interface Message {
    type: "user" | "service" | "loading";
    content: string;
    time: string;
    id: string;
  }

  // 组件属性
  withDefaults(defineProps<Props>(), {
    visible: false,
  });

  // 组件事件
  const emit = defineEmits(["close"]);

  const api = useApi();

  // 响应式数据
  const inputMessage = ref("");
  const messages = ref<Message[]>([]);

  const footRef = ref();

  const state = ref({
    autoScroll: null as any,
  });

  // 获取当前时间字符串
  const getCurrentTime = (): string => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  // 处理关闭弹窗
  const handleClose = () => {
    emit("close");
  };

  const createServiceMessage = (content: string) => {
    messages.value.push({
      type: "service",
      content,
      time: getCurrentTime(),
      id: Common.getUUID(),
    });
  };

  const createUserMessage = (content: string) => {
    messages.value.push({
      type: "user",
      content,
      time: getCurrentTime(),
      id: Common.getUUID(),
    });
  };

  const createLoadingMessage = () => {
    const id = Common.getUUID();
    messages.value.push({
      type: "loading",
      content: "正在思考",
      time: getCurrentTime(),
      id,
    });
    return id;
  };

  //是否在回答中
  const replying = ref(false);

  // 按快捷键发送消息
  const handleKeyCode = (event: KeyboardEvent) => {
    // ctrl + enter 换行
    if (event.keyCode === 13 && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      const elem = event.target as HTMLTextAreaElement;
      // 获取光标位置
      const start = elem.selectionStart;
      const end = elem.selectionEnd;
      // 在光标位置插入换行符
      inputMessage.value = `${inputMessage.value.substring(0, start)}\n${inputMessage.value.substring(end)}`;
      // 使光标移动到插入换行符后面
      nextTick(() => {
        elem.selectionStart = start + 1;
        elem.selectionEnd = start + 1;
      });
      return;
    }
    // enter 发送
    if (event.keyCode === 13) {
      !replying.value && handleSendMessage();
      event.preventDefault();
    }
  };

  const controller = ref();
  // 处理发送消息
  const handleSendMessage = (question = "") => {
    if (replying.value) return message.warning("正在回答中，请稍后");
    if (!inputMessage.value.trim() && !question) return;
    controller.value = new AbortController();
    const text = question || inputMessage.value;
    createUserMessage(text);
    replying.value = true;
    const id = createLoadingMessage();
    nextTick(() => {
      scrollToBottom();
    });

    api.getCustomerAnswer(
      {
        content: text,
      },
      {
        openWhenHidden: true,
        // method: "POST",
        // body: JSON.stringify({ content: text }), // 添加请求体
        signal: controller.value!.signal,
        onopen: async (response: Response) => {
          if (response.ok) {
            openAutoScroll();
          } else if (response.status >= 400 && response.status < 500 && response.status !== 429) {
            console.error("请求错误");
          }
        },
        onmessage: async (sseData: EventSourceMessage) => {
          const { data, event } = sseData;
          if (event === "message") {
            const jsonData = JSON.parse(data);
            let content = jsonData?.payload?.content || "";
            if (jsonData?.type !== "reply") return;
            if (content === text) return;
            const checkImg = /!\[[^\]]*\]\([^)]+\)/.test(content);
            if (!checkImg) {
              content = content.replace(/!\[[^\]]*\]\([^)]*$/g, "");
            } else {
              content = content.replace(/!\[[^\]]*\]\((.*?)\)/g, (match: any, captured: any) => {
                if (captured !== undefined) {
                  return `<img onclick="onPreview('${captured}')" src="${captured}" alt="图片" style="max-width: 100%; height: auto;cursor: pointer;" />`;
                } else {
                  return match;
                }
              });
            }
            const result = {
              id,
              type: "service" as const,
              content: content.replace(/^\n+/, ""),
              time: getCurrentTime(),
            };
            const index = messages.value.findIndex((item) => item.id === id);
            messages.value.splice(index, 1, result);
          }
        },
        onerror: (error: any) => {
          closeAutoScroll();
          replying.value = false;
          console.error("请求错误", error);
        },
        onclose: async () => {
          closeAutoScroll();
          nextTick(() => {
            scrollToBottom();
          });
          replying.value = false;
        },
      },
    );
    inputMessage.value = "";
  };

  const msgListRef = ref();
  // 滚动到底部
  const scrollToBottom = () => {
    const contentEl = document.querySelector(".dialog-content-pine");
    if (contentEl) {
      contentEl.scrollTop = contentEl.scrollHeight;
    }
  };

  const openAutoScroll = () => {
    const listElem = document.querySelector(".dialog-content-pine") as HTMLDivElement;
    listElem.addEventListener("wheel", onMsgListWheel);
    state.value.autoScroll = window.setInterval(() => {
      scrollToBottom();
    }, 200);
  };

  const closeAutoScroll = () => {
    clearInterval(state.value.autoScroll);
    const listElem = msgListRef.value;
    listElem?.removeEventListener("wheel", onMsgListWheel);
  };

  const onMsgListWheel = (e: WheelEvent) => {
    if (e.deltaY < 0) {
      closeAutoScroll();
    }
  };

  const onAsk = (e: any, text: string) => {
    if (replying.value) return message.warning("正在回答中，请稍后");
    if (e instanceof HTMLElement) {
      if (text) {
        handleSendMessage(text);
      }
    }
  };

  const onPreview = (url: string) => {
    // 创建临时图片元素用于预览
    const tempImg = document.createElement("img");
    tempImg.src = url;
    tempImg.style.display = "none";
    document.body.appendChild(tempImg);

    const viewer = new ViewerJS(tempImg, {
      inline: false,
      viewed() {
        // 预览完成后移除临时元素
        document.body.removeChild(tempImg);
      },
      title: false,
      toolbar: {
        zoomIn: 1,
        zoomOut: 1,
        oneToOne: 1,
        reset: 1,
        prev: 0,
        play: 0,
        next: 0,
        rotateLeft: 1,
        rotateRight: 1,
        flipHorizontal: 1,
        flipVertical: 1,
      },
      navbar: false,
      movable: true,
      zoomable: true,
      rotatable: true,
      scalable: true,
      transition: true,
      fullscreen: true,
      keyboard: true,
    });
    viewer.show();
  };

  const welcomeMessage = `终于等到您了，我是您的智能客服-小理，随时解答您对产品的疑问，您也可以直接加<a onclick="onAsk(this,'添加企业微信')">企业微信</a>转人工咨询。\n\n猜您想问：\n `;
  const questions = [
    "得理法搜有哪些核心功能？",
    "得理律助有哪些核心功能？",
    "现在有优惠活动吗？",
    "得理支持第三方集成接入吗？",
  ];
  const tips = computed(() => {
    const questionText = questions.map((t: any, i) => `${i + 1}.<a onclick="onAsk(this,'${t}')">${t}</a>`).join("\n");
    return `${welcomeMessage}${questionText}`;
  });

  onMounted(() => {
    createServiceMessage(tips.value);

    (window as any).onAsk = onAsk;
    (window as any).onPreview = onPreview;
  });

  onUnmounted(() => {
    (window as any).onAsk = null;
    (window as any).onPreview = null;
  });
</script>

<template>
  <div class="online-customer-modal" @touchstart.stop>
    <div class="customer-dialog" :style="{ paddingBottom: `${footRef?.clientHeight || 0}px` }" @click.stop>
      <!-- 头部 -->
      <div class="dialog-header">
        <div class="header-left">
          <div class="header-info">
            <div class="title">
              <span>在线客服 </span><span class="tw-text-12 tw-ml-2 tw-text-[#2f78ff]">(电话：0755-26907610)</span>
            </div>
          </div>
        </div>
        <div class="header-right">
          <button class="close-btn" @click="handleClose">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="#999" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
        </div>
      </div>

      <!-- 消息内容区域 -->
      <div class="dialog-content">
        <div ref="msgListRef" class="dialog-content-pine">
          <!-- 用户消息列表 -->
          <div
            v-for="(msg, index) in messages"
            :key="index"
            class="message-item"
            :class="msg.type === 'user' ? 'user-message' : 'service-message'">
            <div v-if="['service', 'loading'].includes(msg.type)" class="message-avatar">
              <img class="service-avater" src="~/assets/images/ai/user-icon.png" alt="" />
            </div>
            <div v-if="msg.type === 'loading'">
              <div v-if="replying && index === messages.length - 1" class="text-loading message-bubble">
                <span>{{ msg.content }}</span>
              </div>
              <div v-else class="message-bubble">
                <span>无生成内容</span>
              </div>
            </div>
            <div v-else class="message-content">
              <Markdown class="message-bubble" :text="msg.content" />
              <!-- <div class="message-bubble" v-html="message.content"> </div> -->
              <div class="message-time">
                <span v-if="msg.type === 'user'" style="margin-right: 8px">我</span>
                <span>{{ msg.time }}</span>
              </div>
            </div>
            <!-- <div v-if="message.type === 'user'" class="message-avatar">
              <div class="user-avatar">我</div>
            </div> -->
          </div>
        </div>
      </div>

      <!-- 底部输入区域 -->
      <div ref="footRef" class="dialog-footer">
        <div class="input-container">
          <a-textarea
            v-model:value="inputMessage"
            :auto-size="{ minRows: 1, maxRows: 7 }"
            placeholder="请输入您咨询的问题"
            :max-length="200"
            @keydown="handleKeyCode" />
          <div class="send">
            <button class="send-btn" :disabled="!inputMessage.trim() || replying" @click="handleSendMessage()">
              发送
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less">
  @keyframes text-loading {
    0% {
      content: ".";
    }

    33% {
      content: "..";
    }

    66% {
      content: "...";
    }
  }

  /* 响应式设计 */
  @media (max-width: 480px) {
    .customer-dialog {
      width: 90vw;
      height: 80vh;
      margin: 0 5vw;
    }

    .message-content {
      max-width: 220px;
    }
  }

  .text-loading {
    display: flex;
    align-items: center;

    &::after {
      font-weight: 600;
      content: ".";
      animation: text-loading 1s infinite;
    }
  }

  .online-customer-modal {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    overscroll-behavior: contain;
  }

  .customer-dialog {
    display: flex;
    flex-direction: column;
    width: 438px;
    height: 600px;
    overflow: hidden;
    overscroll-behavior: contain;
    // background: #fff;
    // border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 10%);
  }

  /* 头部样式 */
  .dialog-header {
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 55px;
    padding: 0 20px;
    line-height: 55px;

    /* border-bottom: 1px solid #e9ecef; */
    box-shadow: 0 4px 6px 0 rgba(84, 85, 118, 12%);
  }

  .header-left {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  .avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    overflow: hidden;
    background: #fff;
    border-radius: 50%;
  }

  .avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .header-info .title {
    margin-bottom: 2px;
    font-size: 16px;
    font-weight: 600;
    color: #333;
  }

  .header-info .status {
    font-size: 12px;
    color: #28a745;
  }

  .close-btn {
    padding: 4px;
    cursor: pointer;
    background: none;
    border: none;
    border-radius: 4px;
    transition: background-color 0.2s;
  }

  // .close-btn:hover {
  //   background: #e9ecef;
  // }

  /* 内容区域样式 */
  .dialog-content {
    flex: 1;
    padding: 16px;
    padding: 0 6px;
    overflow: hidden;

    /* overflow-y: auto; */

    background: #fff;
  }

  .dialog-content-pine {
    height: 100%;
    padding: 20px;
    padding-top: 44px;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .date-divider {
    margin-bottom: 16px;
    text-align: center;
  }

  .date-divider span {
    padding: 4px 12px;
    font-size: 12px;
    color: #666;
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 12px;
  }

  .message-item {
    display: flex;
    gap: 8px;
    margin-bottom: 44px;
  }

  .message-item.user-message {
    flex-direction: row-reverse;
  }

  .message-avatar {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    overflow: hidden;
    // background: skyblue;
    border-radius: 50%;
  }

  .message-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .user-avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    font-size: 12px;
    font-weight: 600;
    color: #fff;
    background: #007bff;
    border-radius: 50%;
  }

  .message-content {
    position: relative;
    flex: 1;
    max-width: 280px;

    .message-time {
      position: absolute;
      top: -24px;
      left: 0;
    }
  }

  .user-message .message-content {
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    .message-time {
      right: 0;
    }
  }

  .message-bubble {
    padding: 10px;
    font-size: 14px;
    line-height: 1.4;
    color: #1f1f1f;
    word-break: break-word;
    white-space: normal;
  }

  .user-message .message-bubble {
    background: #cbe5fc;
  }

  .service-message .message-bubble {
    background: #f7f6f9;
  }

  .message-time {
    padding: 0 4px;
    font-size: 12px;
    color: #999;
  }

  .user-message .message-time {
    text-align: right;
  }

  /* 底部输入区域样式 */
  .dialog-footer {
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 10;
    width: 100%;
    min-height: 104px;
    padding: 12px 19px 20px;
    // background: #fff;
  }

  .input-container {
    align-items: center;
    min-height: 72px;
    padding: 10px;
    background: #f2f3f7;
    border: 1px solid #e8eaf0;
    border-radius: 12px;

    :deep(.ant-input) {
      &:focus {
        box-shadow: none;
      }
    }
  }

  .message-input {
    padding: 10px 12px;
    font-size: 14px;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    outline: none;
    transition: border-color 0.2s;
  }

  .message-input:focus {
    border-color: #007bff;
  }

  .send {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
  }

  .send-btn {
    height: 26px;
    padding: 0 10px;
    font-size: 14px;
    color: #fff;
    cursor: pointer;
    background: #4285f4;
    border: none;
    border-radius: 20px;
    transition: background-color 0.2s;
  }

  .send-btn:hover:not(:disabled) {
    background: #3367d6;
  }

  .send-btn:disabled {
    cursor: not-allowed;
    background: #ccc;
  }

  /* 滚动条样式 */
  .dialog-content::-webkit-scrollbar {
    width: 4px;
  }

  .dialog-content::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  .dialog-content::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 2px;
  }

  .dialog-content::-webkit-scrollbar-thumb:hover {
    background: #999;
  }

  :deep(.ant-input) {
    padding: 0;
    font-size: 15px;
    line-height: 25px;
    color: #1f1f1f;
    resize: none;
    scrollbar-width: thin;
    background-color: transparent;
    border: none;
    border-radius: 0;
    outline: none;
    transition: none;

    &::-webkit-scrollbar {
      width: 4px;
      height: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #d9d9d9;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
      border-radius: 4px;
    }
  }
</style>
